import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useUrlParam, useUrlNumber, useSetUrlParams, useDebouncedUrlParam } from '@/hooks/useUrlState';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { transactionsApi, type Transaction, type TransactionSummary } from '@/lib/api/client';
import { cn } from '@/lib/utils';

const TYPE_LABELS: Record<string, string> = {
  option_open: 'Option Open',
  option_close: 'Option Close',
  option_expired: 'Expired',
  option_assigned: 'Assigned',
  option_exercised: 'Exercised',
  stock_buy: 'Stock Buy',
  stock_sell: 'Stock Sell',
  stock_sell_short: 'Short Sell',
  stock_split: 'Split',
  reverse_split: 'Reverse Split',
  dividend: 'Dividend',
  interest: 'Interest',
  cash_in: 'Cash In',
  cash_out: 'Cash Out',
  fee: 'Fee',
  other: 'Other',
};

const TYPE_COLORS: Record<string, string> = {
  option_open: 'bg-blue-100 text-blue-700',
  option_close: 'bg-indigo-100 text-indigo-700',
  option_expired: 'bg-gray-100 text-gray-600',
  option_assigned: 'bg-orange-100 text-orange-700',
  option_exercised: 'bg-amber-100 text-amber-700',
  stock_buy: 'bg-green-100 text-green-700',
  stock_sell: 'bg-red-100 text-red-700',
  stock_sell_short: 'bg-rose-100 text-rose-700',
  stock_split: 'bg-purple-100 text-purple-700',
  reverse_split: 'bg-violet-100 text-violet-700',
  dividend: 'bg-emerald-100 text-emerald-700',
  interest: 'bg-teal-100 text-teal-700',
  cash_in: 'bg-sky-100 text-sky-700',
  cash_out: 'bg-slate-100 text-slate-700',
  fee: 'bg-yellow-100 text-yellow-700',
  other: 'bg-gray-100 text-gray-500',
};

type FilterCategory = 'all' | 'options' | 'stock' | 'income' | 'corporate' | 'cash';
type TabView = 'transactions' | 'income' | 'corporate';

const FILTER_CATEGORIES: readonly FilterCategory[] = ['all', 'options', 'stock', 'income', 'corporate', 'cash'];
const TAB_VIEWS: readonly TabView[] = ['transactions', 'income', 'corporate'];
/** Params a filter change invalidates. Stable identity so the debounced setter is stable too. */
const RESET_PAGE = ['page'] as const;

const FILTER_TYPES: Record<FilterCategory, string[]> = {
  all: [],
  options: ['option_open', 'option_close', 'option_expired', 'option_assigned', 'option_exercised'],
  stock: ['stock_buy', 'stock_sell', 'stock_sell_short'],
  income: ['dividend', 'interest'],
  corporate: ['stock_split', 'reverse_split'],
  cash: ['cash_in', 'cash_out', 'fee'],
};

function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
}

// UTC day-key for same-day sort grouping. Matches the prior
// `executedAt.split('T')[0]` semantics for a Z timestamp, but works whether
// executedAt arrives as a Date (revived by apiDateReviver) or a raw ISO
// string (tests, or any future non-fetchApi source).
export function dayKeyFromExecutedAt(executedAt: Date | string): string {
  const d = typeof executedAt === 'string' ? new Date(executedAt) : executedAt;
  return d.toISOString().slice(0, 10);
}

function formatAmount(amount: number): string {
  const prefix = amount >= 0 ? '' : '-';
  return `${prefix}$${Math.abs(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function IncomeView() {
  const [dividends, setDividends] = useState<Transaction[]>([]);
  const [interest, setInterest] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    transactionsApi.income()
      .then(data => {
        setDividends(data.dividends);
        setInterest(data.interest);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;

  // Group dividends by symbol
  const bySymbol = new Map<string, { total: number; payments: Transaction[] }>();
  for (const d of dividends) {
    const sym = d.underlyingSymbol || 'Unknown';
    const entry = bySymbol.get(sym) || { total: 0, payments: [] };
    entry.total += d.amount;
    entry.payments.push(d);
    bySymbol.set(sym, entry);
  }
  const symbolEntries = Array.from(bySymbol.entries()).sort((a, b) => b[1].total - a[1].total);
  const totalDividends = symbolEntries.reduce((sum, [, v]) => sum + v.total, 0);
  const totalInterest = interest.reduce((sum, t) => sum + t.amount, 0);

  return (
    <div className="space-y-6">
      {/* Totals */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">Total Dividends</div>
            <div className="text-2xl font-bold text-emerald-600">{formatAmount(totalDividends)}</div>
            <div className="text-xs text-gray-400">{dividends.length} payments across {bySymbol.size} symbols</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">Total Interest</div>
            <div className="text-2xl font-bold text-teal-600">{formatAmount(totalInterest)}</div>
            <div className="text-xs text-gray-400">{interest.length} payments</div>
          </CardContent>
        </Card>
      </div>

      {/* Dividends by symbol */}
      {symbolEntries.length > 0 && (
        <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold text-gray-700">Dividends by Symbol</h2>
          </CardHeader>
          <CardContent className="p-0">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="py-2 px-4 text-left font-medium text-gray-500">Symbol</th>
                  <th className="py-2 px-4 text-right font-medium text-gray-500">Payments</th>
                  <th className="py-2 px-4 text-right font-medium text-gray-500">Total</th>
                  <th className="py-2 px-4 text-left font-medium text-gray-500">History</th>
                </tr>
              </thead>
              <tbody>
                {symbolEntries.map(([symbol, data]) => (
                  <tr key={symbol} className="border-b border-gray-50">
                    <td className="py-2 px-4 font-medium">{symbol}</td>
                    <td className="py-2 px-4 text-right tabular-nums">{data.payments.length}</td>
                    <td className="py-2 px-4 text-right tabular-nums font-medium text-emerald-600">{formatAmount(data.total)}</td>
                    <td className="py-2 px-4">
                      <div className="flex gap-1 flex-wrap">
                        {data.payments.map(p => (
                          <span key={p.id} className="text-xs text-gray-400" title={`${formatDate(p.executedAt)}: ${formatAmount(p.amount)}`}>
                            {formatDate(p.executedAt)}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Interest payments */}
      {interest.length > 0 && (
        <Card>
          <CardHeader>
            <h2 className="text-sm font-semibold text-gray-700">Interest Payments</h2>
          </CardHeader>
          <CardContent className="p-0">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="py-2 px-4 text-left font-medium text-gray-500">Date</th>
                  <th className="py-2 px-4 text-left font-medium text-gray-500">Action</th>
                  <th className="py-2 px-4 text-right font-medium text-gray-500">Amount</th>
                </tr>
              </thead>
              <tbody>
                {interest.map(t => (
                  <tr key={t.id} className="border-b border-gray-50">
                    <td className="py-2 px-4 tabular-nums">{formatDate(t.executedAt)}</td>
                    <td className="py-2 px-4 text-gray-600">{t.action}</td>
                    <td className="py-2 px-4 text-right tabular-nums text-teal-600 font-medium">{formatAmount(t.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {dividends.length === 0 && interest.length === 0 && (
        <div className="text-center py-12 text-gray-500">No income transactions found.</div>
      )}
    </div>
  );
}

function CorporateActionsView() {
  const [actions, setActions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    transactionsApi.corporateActions()
      .then(data => setActions(data.actions))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;

  if (actions.length === 0) {
    return <div className="text-center py-12 text-gray-500">No stock splits or reverse splits found.</div>;
  }

  return (
    <Card>
      <CardHeader>
        <h2 className="text-sm font-semibold text-gray-700">Splits &amp; Reverse Splits</h2>
      </CardHeader>
      <CardContent className="p-0">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="py-2 px-4 text-left font-medium text-gray-500">Date</th>
              <th className="py-2 px-4 text-left font-medium text-gray-500">Type</th>
              <th className="py-2 px-4 text-left font-medium text-gray-500">Symbol</th>
              <th className="py-2 px-4 text-right font-medium text-gray-500">Shares Added</th>
              <th className="py-2 px-4 text-right font-medium text-gray-500">Post-Split Price</th>
            </tr>
          </thead>
          <tbody>
            {actions.map(t => (
              <tr key={t.id} className="border-b border-gray-50">
                <td className="py-2 px-4 tabular-nums">{formatDate(t.executedAt)}</td>
                <td className="py-2 px-4">
                  <span className={cn(
                    'inline-block px-1.5 py-0.5 rounded text-xs font-medium',
                    TYPE_COLORS[t.transactionType]
                  )}>
                    {TYPE_LABELS[t.transactionType]}
                  </span>
                </td>
                <td className="py-2 px-4 font-medium">{t.underlyingSymbol}</td>
                <td className="py-2 px-4 text-right tabular-nums">{t.quantity.toLocaleString()}</td>
                <td className="py-2 px-4 text-right tabular-nums">{t.price ? `$${t.price.toFixed(2)}` : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

// Causal sort: expired first, then assigned→stock_buy paired, then exercised→stock_sell paired
const TYPE_SORT_ORDER: Record<string, number> = {
  option_close: 1,     // in a roll, close happens first (earlier)
  option_open: 2,      // open happens after close (later, shown at top in desc)
  option_expired: 3,
  option_assigned: 4,
  stock_buy: 5,        // consequence of assignment
  option_exercised: 6,
  stock_sell: 7,       // consequence of exercise
  stock_sell_short: 8,
  stock_split: 9,
  reverse_split: 10,
  dividend: 11,
  interest: 12,
  cash_in: 13,
  cash_out: 14,
  fee: 15,
  other: 16,
};

function sortTransactions(txns: Transaction[]): Transaction[] {
  return [...txns].sort((a, b) => {
    // Same date: sort by type priority, then symbol
    const dateA = dayKeyFromExecutedAt(a.executedAt);
    const dateB = dayKeyFromExecutedAt(b.executedAt);
    if (dateA === dateB) {
      const symA = a.underlyingSymbol || '';
      const symB = b.underlyingSymbol || '';
      if (symA !== symB) return symA.localeCompare(symB);
      const orderA = TYPE_SORT_ORDER[a.transactionType] ?? 15;
      const orderB = TYPE_SORT_ORDER[b.transactionType] ?? 15;
      return orderB - orderA;
    }
    // Different dates: newest first (already sorted by API, but just in case)
    return new Date(b.executedAt).getTime() - new Date(a.executedAt).getTime();
  });
}

export function TransactionsPage() {
  const [txns, setTxns] = useState<Transaction[]>([]);
  const [summary, setSummary] = useState<TransactionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Every filter reads straight from the URL — no local mirror and no sync effect, which is what
  // let this page drift from the convention the chains list follows.
  const [filter] = useUrlParam<FilterCategory>('filter', 'all', FILTER_CATEGORIES);
  // The symbol field keeps up with typing but only reaches the URL — and the API — once the
  // typing stops, through the same debounced hook the chains list uses.
  const [symbolFilter, setSymbolFilter] = useDebouncedUrlParam('symbol', '', 400, { resets: RESET_PAGE });
  const [activeTab, setActiveTab] = useUrlParam<TabView>('tab', 'transactions', TAB_VIEWS);
  const [dateFrom] = useUrlParam('from', '');
  const [dateTo] = useUrlParam('to', '');
  const [page, setPage] = useUrlNumber('page', 1, { min: 1, integer: true });
  const setParams = useSetUrlParams();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Narrowing the result set has to send you back to its first page — page 7 of the unfiltered
  // list is not page 7 of the filtered one. Both params go out in one write because react-router
  // hands a functional updater the params captured at render, so two calls would drop one.
  const setFilterAndResetPage = useCallback((next: FilterCategory) => {
    setParams({ filter: next === 'all' ? null : next, page: null });
  }, [setParams]);
  const setDateRange = useCallback((from: string, to: string) => {
    setParams({ from, to, page: null });
  }, [setParams]);

  useEffect(() => {
    setSummaryLoading(true);
    const types = FILTER_TYPES[filter];
    transactionsApi.summary({
      symbol: symbolFilter || undefined,
      type: types.length > 0 ? types.join(',') : undefined,
      from: dateFrom || undefined,
      to: dateTo || undefined,
    })
      .then(data => setSummary(data))
      .catch(console.error)
      .finally(() => setSummaryLoading(false));
  }, [filter, symbolFilter, dateFrom, dateTo]);

  useEffect(() => {
    if (activeTab !== 'transactions') return;
    setLoading(true);
    const types = FILTER_TYPES[filter];
    transactionsApi.list({
      page,
      limit: 50,
      type: types.length > 0 ? types.join(',') : undefined,
      symbol: symbolFilter || undefined,
      from: dateFrom || undefined,
      to: dateTo || undefined,
    })
      .then(data => {
        setTxns(data.transactions);
        setTotalPages(data.pagination.totalPages);
        setTotal(data.pagination.total);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [page, filter, symbolFilter, dateFrom, dateTo, activeTab]);

  // Selection is row identity, not view state: it means nothing once the rows change. Paging
  // clears it too, which the pre-URL version did not do — the old selection referred to rows
  // that are no longer on screen, and its totals bar was quietly summing them.
  useEffect(() => { setSelectedIds(new Set()); }, [filter, symbolFilter, dateFrom, dateTo, page]);

  // Sort transactions for display
  const sortedTxns = useMemo(() => sortTransactions(txns), [txns]);

  // Compute P&L for a transaction (FIFO for stock, amount for options)
  function txnPnl(txn: Transaction): number {
    if (txn.costBasis != null && txn.quantity > 0) {
      return (txn.price - txn.costBasis) * txn.quantity;
    }
    if (txn.transactionType === 'option_open' || txn.transactionType === 'option_close' ||
        txn.transactionType === 'option_expired') {
      return txn.amount || 0;
    }
    return 0;
  }

  // Compute selected totals
  const selectedTotal = useMemo(() => {
    if (selectedIds.size === 0) return null;
    let amount = 0;
    let pnl = 0;
    let fees = 0;
    for (const txn of txns) {
      if (selectedIds.has(txn.id)) {
        amount += txn.amount;
        pnl += txnPnl(txn);
        fees += txn.fees;
      }
    }
    return {
      amount: Math.round(amount * 100) / 100,
      pnl: Math.round(pnl * 100) / 100,
      fees: Math.round(fees * 100) / 100,
      count: selectedIds.size,
    };
  }, [selectedIds, txns]);

  // Compute page-level totals for filtered view
  const pageTotals = useMemo(() => {
    let amount = 0;
    let pnl = 0;
    let fees = 0;
    for (const txn of txns) {
      amount += txn.amount;
      pnl += txnPnl(txn);
      fees += txn.fees;
    }
    return {
      amount: Math.round(amount * 100) / 100,
      pnl: Math.round(pnl * 100) / 100,
      fees: Math.round(fees * 100) / 100,
    };
  }, [txns]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Transactions</h1>
        {!summaryLoading && summary && (
          <span className="text-sm text-gray-500">
            {summary.totalTransactions.toLocaleString()} total transactions
          </span>
        )}
      </div>

      {/* Tab navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6" role="tablist" data-testid="tabs">
          {([
            { key: 'transactions', label: 'All Transactions' },
            { key: 'income', label: 'Income' },
            { key: 'corporate', label: 'Corporate Actions' },
          ] as { key: TabView; label: string }[]).map(tab => (
            <button
              key={tab.key}
              role="tab"
              onClick={() => setActiveTab(tab.key)}
              aria-selected={activeTab === tab.key}
              className={cn(
                'pb-3 text-sm font-medium border-b-2 transition-colors',
                activeTab === tab.key
                  ? 'border-brand-600 text-brand-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Income tab */}
      {activeTab === 'income' && <IncomeView />}

      {/* Corporate actions tab */}
      {activeTab === 'corporate' && <CorporateActionsView />}

      {/* Transactions tab */}
      {activeTab === 'transactions' && (
        <>
          {/* Summary cards */}
          {!summaryLoading && summary && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {summary.byType
                .filter(t => t.count > 0)
                .sort((a, b) => b.count - a.count)
                .slice(0, 8)
                .map(t => (
                  <Card key={t.type} className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => {
                      // Find which category contains this type
                      for (const [cat, types] of Object.entries(FILTER_TYPES)) {
                        if (types.includes(t.type)) {
                          setFilterAndResetPage(cat as FilterCategory);
                          return;
                        }
                      }
                    }}
                  >
                    <CardContent className="p-3">
                      <div className="text-xs text-gray-500">{TYPE_LABELS[t.type] || t.type}</div>
                      <div className="text-lg font-semibold">{t.count}</div>
                      <div className={cn('text-xs', t.totalAmount >= 0 ? 'text-green-600' : 'text-red-600')}>
                        {formatAmount(t.totalAmount)}
                      </div>
                    </CardContent>
                  </Card>
                ))}
            </div>
          )}

          {/* Dividend summary */}
          {!summaryLoading && summary && summary.dividends.total > 0 && (
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('income')}>
              <CardHeader>
                <h2 className="text-sm font-semibold text-gray-700">Dividend Income</h2>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="flex items-center gap-4 flex-wrap">
                  <span className="text-lg font-bold text-emerald-600">
                    {formatAmount(summary.dividends.total)}
                  </span>
                  <span className="text-xs text-gray-400">|</span>
                  {summary.dividends.bySymbol.slice(0, 10).map(d => (
                    <span key={d.symbol} className="text-xs text-gray-600">
                      <span className="font-medium">{d.symbol}</span>
                      {' '}{formatAmount(d.total)}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        {(['all', 'options', 'stock', 'income', 'corporate', 'cash'] as FilterCategory[]).map(cat => (
          <Button
            key={cat}
            variant={filter === cat ? 'primary' : 'secondary'}
            size="sm"
            aria-pressed={filter === cat}
            onClick={() => setFilterAndResetPage(cat)}
          >
            {cat.charAt(0).toUpperCase() + cat.slice(1)}
          </Button>
        ))}
        <input
          type="text"
          placeholder="Symbol..."
          value={symbolFilter}
          data-testid="symbol-filter"
          onChange={e => setSymbolFilter(e.target.value.toUpperCase())}
          className="ml-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 w-24"
        />
        <input
          type="date"
          value={dateFrom}
          data-testid="date-from"
          onChange={e => setDateRange(e.target.value, dateTo)}
          className="ml-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
        <span className="text-xs text-gray-400">to</span>
        <input
          type="date"
          value={dateTo}
          data-testid="date-to"
          onChange={e => setDateRange(dateFrom, e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
        {(dateFrom || dateTo) && (
          <Button size="sm" variant="ghost" onClick={() => setDateRange('', '')}>
            Clear dates
          </Button>
        )}
      </div>

      {/* Transaction table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : txns.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {total === 0 ? 'No transactions yet. Import a Schwab export to get started.' : 'No transactions match this filter.'}
            </div>
          ) : (
            <>
              {/* Selection summary bar */}
              {selectedTotal && (
                <div className="flex items-center justify-between px-4 py-2 bg-brand-50 border-b border-brand-100">
                  <span className="text-sm font-medium text-brand-700">
                    {selectedTotal.count} selected
                  </span>
                  <div className="flex items-center gap-4 text-sm">
                    <span>
                      Amount: <span className={cn('font-semibold', selectedTotal.amount >= 0 ? 'text-green-600' : 'text-red-600')}>
                        {formatAmount(selectedTotal.amount)}
                      </span>
                    </span>
                    {selectedTotal.pnl !== 0 && (
                      <span>
                        P&L: <span className={cn('font-semibold', selectedTotal.pnl >= 0 ? 'text-green-600' : 'text-red-600')}>
                          {formatAmount(selectedTotal.pnl)}
                        </span>
                      </span>
                    )}
                    {selectedTotal.fees > 0 && (
                      <span>
                        Fees: <span className="font-medium text-gray-600">${selectedTotal.fees.toFixed(2)}</span>
                      </span>
                    )}
                    <button
                      onClick={() => setSelectedIds(new Set())}
                      className="text-brand-600 hover:text-brand-800 text-xs underline"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="py-2 px-2 w-8">
                        <input
                          type="checkbox"
                          checked={sortedTxns.length > 0 && selectedIds.size === sortedTxns.length}
                          onChange={e => {
                            if (e.target.checked) {
                              setSelectedIds(new Set(sortedTxns.map(t => t.id)));
                            } else {
                              setSelectedIds(new Set());
                            }
                          }}
                          className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                        />
                      </th>
                      <th className="py-2 px-3 text-left font-medium text-gray-500">Date</th>
                      <th className="py-2 px-3 text-left font-medium text-gray-500">Type</th>
                      <th className="py-2 px-3 text-left font-medium text-gray-500">Symbol</th>
                      <th className="py-2 px-3 text-left font-medium text-gray-500">Action</th>
                      <th className="py-2 px-3 text-right font-medium text-gray-500">Qty</th>
                      <th className="py-2 px-3 text-right font-medium text-gray-500">Price</th>
                      <th className="py-2 px-3 text-right font-medium text-gray-500">Amount</th>
                      <th className="py-2 px-3 text-right font-medium text-gray-500">P&L</th>
                      <th className="py-2 px-3 text-right font-medium text-gray-500">Fees</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTxns.map(txn => (
                      <tr
                        key={txn.id}
                        className={cn(
                          'border-b border-gray-50 hover:bg-gray-50 cursor-pointer',
                          selectedIds.has(txn.id) && 'bg-brand-50/50'
                        )}
                        onClick={() => {
                          setSelectedIds(prev => {
                            const next = new Set(prev);
                            if (next.has(txn.id)) next.delete(txn.id);
                            else next.add(txn.id);
                            return next;
                          });
                        }}
                      >
                        <td className="py-2 px-2" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(txn.id)}
                            onChange={() => {
                              setSelectedIds(prev => {
                                const next = new Set(prev);
                                if (next.has(txn.id)) next.delete(txn.id);
                                else next.add(txn.id);
                                return next;
                              });
                            }}
                            className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                          />
                        </td>
                        <td className="py-2 px-3 whitespace-nowrap tabular-nums">
                          {formatDate(txn.executedAt)}
                        </td>
                        <td className="py-2 px-3">
                          <span className={cn(
                            'inline-block px-1.5 py-0.5 rounded text-xs font-medium',
                            TYPE_COLORS[txn.transactionType] || 'bg-gray-100 text-gray-500'
                          )}>
                            {TYPE_LABELS[txn.transactionType] || txn.transactionType}
                          </span>
                        </td>
                        <td className="py-2 px-3 font-medium">
                          <div className="flex items-center gap-1">
                            {txn.underlyingSymbol ? (
                              <Link
                                to={`/symbols/${txn.underlyingSymbol}`}
                                className="text-brand-600 hover:text-brand-800 hover:underline"
                                onClick={e => e.stopPropagation()}
                              >
                                {txn.underlyingSymbol}
                              </Link>
                            ) : '-'}
                            {txn.optionType && txn.strikePrice && (
                              <span className="text-xs text-gray-400">
                                {txn.strikePrice}{txn.optionType === 'call' ? 'C' : 'P'}
                              </span>
                            )}
                            {txn.tradeChainId && (
                              <Link
                                to={`/chains/${txn.tradeChainId}`}
                                className="text-gray-400 hover:text-brand-600 ml-0.5"
                                onClick={e => e.stopPropagation()}
                                title="View chain"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                </svg>
                              </Link>
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-3 text-gray-600">{txn.action}</td>
                        <td className="py-2 px-3 text-right tabular-nums">
                          {txn.quantity || '-'}
                        </td>
                        <td className="py-2 px-3 text-right tabular-nums">
                          {txn.price ? `$${txn.price.toFixed(2)}` : '-'}
                        </td>
                        <td className={cn(
                          'py-2 px-3 text-right tabular-nums font-medium',
                          txn.amount > 0 ? 'text-green-600' : txn.amount < 0 ? 'text-red-600' : ''
                        )}>
                          {txn.amount ? formatAmount(txn.amount) : '-'}
                        </td>
                        <td className="py-2 px-3 text-right tabular-nums">
                          {txn.costBasis != null && txn.quantity > 0 ? (() => {
                            const pnl = (txn.price - txn.costBasis) * txn.quantity;
                            const rounded = Math.round(pnl * 100) / 100;
                            return (
                              <span className={cn('font-medium', rounded >= 0 ? 'text-green-600' : 'text-red-600')}>
                                {formatAmount(rounded)}
                              </span>
                            );
                          })() : (
                            txn.transactionType === 'option_close' || txn.transactionType === 'option_expired' ? (
                              <span className={cn('font-medium', txn.amount >= 0 ? 'text-green-600' : 'text-red-600')}>
                                {txn.amount ? formatAmount(txn.amount) : '-'}
                              </span>
                            ) : (
                              <span className="text-gray-300">-</span>
                            )
                          )}
                        </td>
                        <td className="py-2 px-3 text-right tabular-nums text-gray-400">
                          {txn.fees ? `$${txn.fees.toFixed(2)}` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t" data-testid="pagination">
                  <span className="text-sm text-gray-500">
                    Page {page} of {totalPages} ({total.toLocaleString()} results)
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => setPage(page - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={page >= totalPages}
                      onClick={() => setPage(page + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

          {/* Page totals */}
          {txns.length > 0 && (
            <div className="flex justify-end gap-6 text-sm text-gray-500">
              <span>
                Amount: <span className={cn('font-medium', pageTotals.amount >= 0 ? 'text-green-600' : 'text-red-600')}>
                  {formatAmount(pageTotals.amount)}
                </span>
              </span>
              {pageTotals.pnl !== 0 && (
                <span>
                  P&L: <span className={cn('font-medium', pageTotals.pnl >= 0 ? 'text-green-600' : 'text-red-600')}>
                    {formatAmount(pageTotals.pnl)}
                  </span>
                </span>
              )}
              {pageTotals.fees > 0 && (
                <span>
                  Fees: <span className="font-medium text-gray-700">${pageTotals.fees.toFixed(2)}</span>
                </span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
